import { createSlice } from '@reduxjs/toolkit';
import addDays from 'date-fns/addDays';
import {
  getInstitutionUsers,
  getSubmissions,
  getSubmissionsCalendar,
  uploadListFile,
} from './thunks';
import { isValidSearchInput } from "utils/stringUtils";

export const initialState = {
  submissionsList: [],
  submissionsListLoading: false,
  submissionsListError: false,
  submissionsListCurrentPage: 0,
  submissionsListPageSize: 10,
  submissionsListTotalPages: 0,
  submissionsListTotalElements: 0,
  submissionsCalendarDate: new Date(),
  submissionsCalendar: [],
  submissionsCalendarLoading: false,
  submissionsCalendarError: false,
  institutionUsers: [],
  institutionUsersLoading: false,
  institutionUsersError: false,
  institution: null,
  reporter: null,
  obligation: '',
  status: ['ALL'],
  startDate: new Date(),
  endDate: addDays(new Date(), 30),
  showAllProblematic: true,
  type: 'ALL',
  fileUploaderState: { fileUploaderOpen: false },
  listFileUploading: false,
};

const slice = createSlice({
  name: 'submissions',
  initialState: initialState,
  reducers: {
    resetSubmissionsState: () => initialState,
    setFileUploaderState: (state, { payload }) => {
      state.fileUploaderState = payload;
    },
    setSubmissionsCalendarDate: (state, { payload }) => {
      state.submissionsCalendarDate = payload;
    },
    setReporter: (state, { payload }) => {
      state.reporter = payload;
      state.submissionsListCurrentPage = 0;
    },
    setInstitution: (state, { payload }) => {
      state.institution = payload;
      state.submissionsListCurrentPage = 0;
    },
    setObligation: (state, { payload }) => {
      state.obligation = payload;
      if (isValidSearchInput(payload)) {
        state.submissionsListCurrentPage = 0;
      }
    },
    setStatus: (state, { payload }) => {
      const lastAddedValue = payload && payload[payload.length - 1];

      state.status =
        payload && payload.length > 0
          ? lastAddedValue === 'ALL'
            ? payload.filter(value => value === 'ALL')
            : payload.filter(value => value !== 'ALL')
          : ['ALL'];
      state.submissionsListCurrentPage = 0;
    },
    setStartDate: (state, { payload }) => {
      state.startDate = payload;
      state.submissionsListCurrentPage = 0;
    },
    setEndDate: (state, { payload }) => {
      state.endDate = payload;
      state.submissionsListCurrentPage = 0;
    },
    setType: (state, { payload }) => {
      state.type = payload;
      state.submissionsListCurrentPage = 0;
    },
    setShowAllProblematic: (state, { payload }) => {
      state.showAllProblematic = payload;
      state.submissionsListCurrentPage = 0;
    },
    setSubmissionsListCurrentPage: (state, { payload }) => {
      state.submissionsListCurrentPage = payload;
    },
    setSubmissionsListPageSize: (state, { payload }) => {
      state.submissionsListPageSize = payload;
      state.submissionsListCurrentPage = 0;
    },
  },
  extraReducers: {
    [getSubmissions.fulfilled]: (state, { payload }) => {
      state.submissionsList = payload.content;
      state.submissionsListTotalPages = payload.totalPages;
      state.submissionsListTotalElements = payload.totalElements;
      state.submissionsListLoading = false;
      state.submissionsListError = false;
    },
    [getSubmissions.pending]: state => {
      state.submissionsList = [];
      state.submissionsListTotalPages = 0;
      state.submissionsListTotalElements = 0;
      state.submissionsListLoading = true;
      state.submissionsListError = false;
    },
    [getSubmissions.rejected]: state => {
      state.submissionsList = [];
      state.submissionsListTotalPages = 0;
      state.submissionsListTotalElements = 0;
      state.submissionsListLoading = false;
      state.submissionsListError = true;
    },
    [getSubmissionsCalendar.fulfilled]: (state, { payload }) => {
      state.submissionsCalendar = payload;
      state.submissionsCalendarLoading = false;
      state.submissionsCalendarError = false;
    },
    [getSubmissionsCalendar.pending]: state => {
      state.submissionsCalendar = [];
      state.submissionsCalendarLoading = true;
      state.submissionsCalendarError = false;
    },
    [getSubmissionsCalendar.rejected]: state => {
      state.submissionsCalendar = [];
      state.submissionsCalendarLoading = false;
      state.submissionsCalendarError = true;
    },
    [getInstitutionUsers.fulfilled]: (state, { payload }) => {
      state.institutionUsers = payload;
      state.institutionUsersLoading = false;
      state.institutionUsersError = false;
    },
    [getInstitutionUsers.pending]: state => {
      state.institutionUsers = [];
      state.institutionUsersLoading = true;
      state.institutionUsersError = false;
    },
    [getInstitutionUsers.rejected]: state => {
      state.institutionUsers = [];
      state.institutionUsersLoading = false;
      state.institutionUsersError = true;
    },
    [uploadListFile.fulfilled]: state => {
      state.listFileUploading = false;
    },
    [uploadListFile.pending]: state => {
      state.listFileUploading = true;
    },
    [uploadListFile.rejected]: state => {
      state.listFileUploading = false;
    },
  },
});

export const {
  resetSubmissionsState,
  setSubmissionsCalendarDate,
  setReporter,
  setInstitution,
  setObligation,
  setStatus,
  setStartDate,
  setEndDate,
  setType,
  setSubmissionsListCurrentPage,
  setSubmissionsListPageSize,
  setFileUploaderState,
  setShowAllProblematic,
} = slice.actions;

export default slice.reducer;
